import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import TextareaAutosize from "react-textarea-autosize";
import {useForm, Controller} from "react-hook-form";
import "./Edit.scss";
import {Modal} from "../../modal/Modal";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {
  clearConfiguration,
  clearValidation,
  editAsync,
  getConfigurationAsync,
  getUserGroupsAsync,
  getUserTypesAsync,
  selectConfiguration,
  selectConfigurationStatus,
  selectEditStatus,
  selectEditValidationErrors,
  selectUserGroups,
  selectUserGroupsStatus,
  selectUserTypes,
  selectUserTypesStatus
} from "./editSlice";
import {SpinnerButton} from "../../button/SpinnerButton";
import {stringToNumber} from "../../../infrastructure/number";
import {isIdValid} from "./validate";
import {sortByDescription, sortUserGroups} from "../sort";
import {validateFileInput} from "../validate";
import {getDistinct, getFullPath, isPairUnavailable} from "../mappers";
import {type Configuration, ConfigurationFieldName, type UserGroup, type UserType} from "../types";
import {fetchListAsync, selectConfigurations, selectStatus} from "../list/listSlice";
import {
  applicabilityMatrixError,
  applicablityAccessError,
  defaultUserGroupDescription,
  defaultUserTypeDescription,
  descriptionMaxLength,
  descriptionMaxLengthValidation,
  genericError,
  nameMaxLength,
  nameMaxLengthValidation,
  requiredFieldValidation
} from "../constants";
import {hasValidationMessage} from "../common/hasValidationMessage";
import {ValidationMessage} from "../common/ValidationMessage";
import {editFailedText, editSuccessText} from "./constants";
import {formatTimeWithoutMilliseconds} from "../common/timeWithoutMilliseconds";
import {AddUserGroup} from "../common/AddUserGroup";
import {isFailed, isLoading} from "../../../infrastructure/status";
import {displayUserMessage} from "../../message-panel/messagesSlice";
import {errorUserMessage, successUserMessage} from "../../message-panel/actions";
import {
  getOtherUserGroups,
  getOtherUserTypes,
  getUnavailableTypeGroupPairs,
  hasConfigurationChanged
} from "./helpers";
import {deselectAll, selectAll} from "../common/selectDeselectAll";
import {onUserTypeCheckboxChange} from "../common/onUserTypeCheckboxChange";
import {resetValidation} from "./hooks";
import {noAvailableUserGroups} from "../common/constants";

export const Edit = () => {
  const {id} = useParams();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const allConfigurations = useAppSelector(selectConfigurations) ?? [];
  const allConfigurationsStatus = useAppSelector(selectStatus);
  const configurationStatus = useAppSelector(selectConfigurationStatus);

  useEffect(() => {
    (!isIdValid(id) || isFailed(configurationStatus)) && navigate("/", {replace: true});
  }, [id, navigate, configurationStatus]);

  const {
    register,
    handleSubmit,
    formState: {errors = {}},
    reset,
    getValues,
    setValue,
    control,
    clearErrors
  } = useForm<Configuration>();

  useEffect(() => {
    dispatch(clearValidation({}));
    dispatch(fetchListAsync());
    dispatch(getUserTypesAsync());
    dispatch(getUserGroupsAsync());
    dispatch(getConfigurationAsync(stringToNumber(id)))
      .unwrap()
      .catch(err => {
        dispatch(displayUserMessage(errorUserMessage(err?.message).payload));
      });
    return () => {
      dispatch(clearConfiguration());
    };
  }, [id, dispatch]);

  const editStatus = useAppSelector(selectEditStatus);
  const userGroups = sortUserGroups(useAppSelector(selectUserGroups));
  const userGroupsStatus = useAppSelector(selectUserGroupsStatus);
  const userTypes: UserType[] = sortByDescription(useAppSelector(selectUserTypes));
  const userTypesStatus = useAppSelector(selectUserTypesStatus);
  const configuration = useAppSelector(selectConfiguration);

  const [newUserGroups, setNewUserGroups] = useState<UserGroup[]>([]);
  const [originalConfiguration, setOriginalConfiguration] = useState<Configuration>();
  const [isAddUserGroupDirty, setIsAddUserGroupDirty] = useState<boolean>(false);

  const validationErrors = useAppSelector(selectEditValidationErrors);
  const clearValidationErrors = () => {
    dispatch(clearValidation({name: "typeGroupPairs"}));
  };

  const otherUserGroups: UserGroup[] = getOtherUserGroups(configuration, userGroups);

  const otherUserTypes: UserType[] = getOtherUserTypes(configuration, userTypes);

  const distinctConfigurationUserGroups = getDistinct([...userGroups, ...otherUserGroups]);
  const distinctAllUserGroups = getDistinct([...distinctConfigurationUserGroups, ...newUserGroups]);
  const distinctAllUserTypes = getDistinct([...userTypes, ...otherUserTypes]);

  const unavailableTypeGroupPairs = getUnavailableTypeGroupPairs(allConfigurations, configuration);

  resetValidation(
    reset,
    configuration,
    distinctConfigurationUserGroups,
    distinctAllUserTypes,
    unavailableTypeGroupPairs
  );

  const values = getValues();

  useEffect(() => {
    if (!originalConfiguration && values.id) {
      setOriginalConfiguration(values);
    }
  }, [values.id, setOriginalConfiguration, originalConfiguration]);

  const hasChanged = hasConfigurationChanged(values, originalConfiguration) || isAddUserGroupDirty;

  const goBack = () => {
    navigate(-1);
  };

  const cancelChanges = () => {
    !hasChanged && goBack();
  };

  const onSubmitFailure = () => {
    dispatch(displayUserMessage(errorUserMessage(editFailedText).payload));
  };

  const onSubmit = (configuration: Configuration) => {
    dispatch(editAsync(configuration))
      .unwrap()
      .then(() => {
        dispatch(displayUserMessage(successUserMessage(editSuccessText).payload));
        navigate("/");
      })
      .catch(err => {
        dispatch(displayUserMessage(errorUserMessage(err?.message).payload));
      });
  };

  const isLoadingApplicability =
    isLoading(userGroupsStatus) ||
    isLoading(userTypesStatus) ||
    isLoading(configurationStatus) ||
    isLoading(allConfigurationsStatus);
  const hasLoadingApplicabilityFailed = isFailed(userTypesStatus) || isFailed(userGroupsStatus);

  const hasAnyPairAvailable =
    configuration?.typeGroupPairs?.length > 0 ||
    newUserGroups?.length * userTypes?.length >
      unavailableTypeGroupPairs?.filter(
        pair =>
          newUserGroups.find(group => group.code === pair.groupId) &&
          userTypes?.find(type => type.code === pair.typeId)
      )?.length ||
    unavailableTypeGroupPairs?.filter(
      pair =>
        userGroups?.find(group => group.code === pair.groupId) &&
        userTypes?.find(type => type.code === pair.typeId)
    ).length <
      userGroups?.length * userTypes?.length;

  const typeGroupEntries = Object.entries(values).filter(entry => entry[0].startsWith("type_"));

  const currentUserGroupsUnavailable = unavailableTypeGroupPairs?.filter(
    pair =>
      userGroups?.find(group => group.code === pair.groupId) ||
      newUserGroups?.find(group => group.code === pair.groupId) ||
      (otherUserGroups?.find(group => group.code === pair.groupId) &&
        userTypes?.find(type => type.code === pair.typeId)) ||
      otherUserTypes?.find(type => type.code === pair.typeId)
  );

  const areAllTypeGroupPairsSelected =
    !typeGroupEntries.some(entry => !entry[1]) &&
    distinctAllUserTypes.length * distinctAllUserGroups.length -
      currentUserGroupsUnavailable.length ===
      typeGroupEntries.length;

  const setNewUserGroupsWrapper = (userGroups: UserGroup[]) => {
    setNewUserGroups(userGroups);
    const existingPairs = allConfigurations.flatMap(configuration => configuration.typeGroupPairs);
    const groupId = userGroups[userGroups.length - 1].code;
    userTypes.forEach(userType => {
      !existingPairs.find(pair => pair.typeId === userType.code && pair.groupId === groupId) &&
        setValue(`type_${userType.code}_group_${groupId}`, false);
    });
  };

  return (
    <div className="container">
      <div className="configurations-container title">
        <h2>Edit configuration #{id}</h2>
      </div>
      {isLoadingApplicability && (
        <div className="loader-wrap light-background">
          <i className="fad fa-spinner-third fa-spin icon-xxl spinner spinner-page dark"></i>
        </div>
      )}
      {!isLoadingApplicability && (
        <div className="configurations-container">
          <div className="edit-tags">
            <label className="badge badge-info-sandstone badge-medium">
              Modified on {formatTimeWithoutMilliseconds(configuration?.lastUpdate)}
            </label>
          </div>
          <div id="form-inputs">
            <form className="input-dark">
              <div className="form-group medium">
                <label htmlFor="name" className="required">
                  Name
                </label>
                <Controller
                  control={control}
                  name={ConfigurationFieldName.name}
                  rules={{required: true, maxLength: nameMaxLength}}
                  render={({field: {onChange, onBlur, value}}) => {
                    const clearValidationAndChange = (
                      value: React.ChangeEvent<HTMLTextAreaElement>
                    ) => {
                      clearErrors(ConfigurationFieldName.name);
                      dispatch(clearValidation({name: ConfigurationFieldName.name}));
                      onChange(value);
                    };
                    return (
                      <TextareaAutosize
                        onChange={clearValidationAndChange}
                        onBlur={onBlur}
                        value={value}
                        id="name"
                        className={`form-control required ${
                          errors.name || hasValidationMessage(validationErrors.name)
                            ? "is-invalid"
                            : ""
                        }`}
                        placeholder="Name"
                      />
                    );
                  }}
                />
                {errors.name && (
                  <div className="invalid-feedback">
                    {errors.name.type === "maxLength"
                      ? nameMaxLengthValidation()
                      : requiredFieldValidation}
                  </div>
                )}
                <ValidationMessage errors={validationErrors.name} />
              </div>
              <div className="form-group medium">
                <label htmlFor="description">Description</label>
                <Controller
                  control={control}
                  name={ConfigurationFieldName.description}
                  rules={{maxLength: descriptionMaxLength}}
                  render={({field: {onChange, onBlur, value}}) => {
                    const clearValidationAndChange = (
                      value: React.ChangeEvent<HTMLTextAreaElement>
                    ) => {
                      clearErrors(ConfigurationFieldName.description);
                      dispatch(clearValidation({name: ConfigurationFieldName.description}));
                      onChange(value);
                    };
                    return (
                      <TextareaAutosize
                        onChange={clearValidationAndChange}
                        onBlur={onBlur}
                        value={value}
                        id="description"
                        className={`form-control ${
                          errors.description || hasValidationMessage(validationErrors.description)
                            ? "is-invalid"
                            : ""
                        }`}
                        placeholder="Description"
                      />
                    );
                  }}
                />
                {errors.description && (
                  <div className="invalid-feedback">
                    {errors.description.type === "maxLength"
                      ? descriptionMaxLengthValidation()
                      : genericError}
                  </div>
                )}
                <ValidationMessage errors={validationErrors.description} />
              </div>
              <div className="form-group medium">
                <label htmlFor="FileInput">Zip file</label>
                <input
                  type="file"
                  id="FileInput"
                  className={`form-control required ${
                    errors.file || hasValidationMessage(validationErrors.file) ? "is-invalid" : ""
                  } ${
                    !(getValues?.()?.file?.length > 0)
                      ? "fallback-filename"
                      : "invisible-fallback-filename"
                  }`}
                  placeholder="Upload a file"
                  {...register(ConfigurationFieldName.file, {
                    validate: validateFileInput,
                    onChange: () => {
                      clearErrors(ConfigurationFieldName.file);
                      dispatch(clearValidation({name: ConfigurationFieldName.file}));
                    }
                  })}
                />
                {errors.file && (
                  <div className="invalid-feedback">
                    {errors.file.message || requiredFieldValidation}
                  </div>
                )}
                <ValidationMessage errors={validationErrors.file} />
              </div>
              <label>Applicability</label>
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th style={{whiteSpace: "nowrap"}} className="">
                      <div className="ellipsis-header">&nbsp;</div>
                    </th>
                    {distinctAllUserTypes.map(userType => (
                      <th key={userType.code} style={{whiteSpace: "nowrap"}} className="">
                        <div className="ellipsis-header applicability-column-header">
                          {userType.description ?? defaultUserTypeDescription(userType.code)}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {distinctAllUserGroups.length > 0 ? (
                    distinctAllUserGroups.map(userGroup => (
                      <tr
                        key={userGroup.code}
                        className="stable-row"
                        id="configuration-details-table">
                        <td
                          className={`left-column ${
                            !userGroup.description ? "warning-highlight" : ""
                          }`}>
                          {userGroup.description
                            ? getFullPath(userGroup)
                            : defaultUserGroupDescription(userGroup.code)}
                        </td>
                        {distinctAllUserTypes.map(userType => {
                          const userTypeValidation = [
                            validationErrors.typeGroupPairs?.find(
                              error =>
                                error.parameters?.typeId === userType.code &&
                                error.parameters?.groupId === userGroup.code
                            )
                          ];

                          return (
                            <td
                              key={userType.code}
                              className={`regular-column ${
                                isPairUnavailable(unavailableTypeGroupPairs, userType, userGroup)
                                  ? "default-cursor"
                                  : ""
                              }`}>
                              <div className="form-group custom-control custom-checkbox small applicability-checkbox">
                                {isPairUnavailable(
                                  unavailableTypeGroupPairs,
                                  userType,
                                  userGroup
                                ) ? null : (
                                  <>
                                    <input
                                      type="checkbox"
                                      className={`custom-control-input ${
                                        hasValidationMessage(userTypeValidation) ? "is-invalid" : ""
                                      }`}
                                      id={`type_${userType.code}_group_${userGroup.code}`}
                                      {...register(
                                        `type_${userType.code}_group_${userGroup.code}`,
                                        {
                                          onChange: onUserTypeCheckboxChange(
                                            dispatch,
                                            userType,
                                            userGroup,
                                            clearValidation
                                          )
                                        }
                                      )}
                                    />
                                    <label
                                      className="custom-control-label"
                                      htmlFor={`type_${userType.code}_group_${userGroup.code}`}>
                                      &nbsp;
                                    </label>
                                    <ValidationMessage errors={userTypeValidation} />
                                  </>
                                )}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  ) : (
                    <div className="no-available-user-groups">{noAvailableUserGroups}</div>
                  )}
                  <AddUserGroup
                    userTypes={distinctAllUserTypes}
                    userGroups={distinctAllUserGroups}
                    newUserGroups={newUserGroups}
                    setNewUserGroups={setNewUserGroupsWrapper}
                    isDirty={isAddUserGroupDirty}
                    setIsDirty={setIsAddUserGroupDirty}
                  />
                </tbody>
              </table>
              {hasAnyPairAvailable && (
                <button
                  type="button"
                  onClick={
                    areAllTypeGroupPairsSelected
                      ? deselectAll(
                          typeGroupEntries,
                          setValue,
                          clearValidationErrors,
                          unavailableTypeGroupPairs
                        )
                      : selectAll(typeGroupEntries, setValue, unavailableTypeGroupPairs)
                  }
                  className="btn btn-secondary btn-dark btn-select-deselect-all">
                  <span className="btn-select-deselect-all__text">
                    {areAllTypeGroupPairsSelected
                      ? "Deselect all applicabilities"
                      : "Select all applicabilities"}
                  </span>
                </button>
              )}
              {distinctAllUserGroups.some(userGroup => !userGroup.description) ? (
                <div className="warning-highlight applicability-access-error">
                  {applicablityAccessError}
                </div>
              ) : null}
              {(!hasAnyPairAvailable || hasLoadingApplicabilityFailed) &&
                hasLoadingApplicabilityFailed && (
                  <div>
                    <div className="block bg-sky-blue-lightest">
                      <div className="no-configurations">
                        <h4 className="no-configurations-text">
                          <span>{applicabilityMatrixError}</span>
                        </h4>
                      </div>
                    </div>
                  </div>
                )}
              <div className="buttons action-buttons">
                <Modal
                  title={"Discard changes"}
                  body={"Do you want to go back without saving changes?"}
                  onContinue={goBack}
                  disabled={!hasChanged || isLoading(editStatus)}
                  showOnTabClose={true}
                  primaryButtonText="Discard">
                  <button
                    type="button"
                    onClick={isLoading(editStatus) ? undefined : cancelChanges}
                    className="btn btn-secondary btn-dark btn-configuration-view">
                    Cancel
                  </button>
                </Modal>
                <SpinnerButton
                  onClick={
                    isLoading(editStatus) ? undefined : handleSubmit(onSubmit, onSubmitFailure)
                  }
                  className="btn-configuration-view"
                  showSpinner={isLoading(editStatus)}>
                  Save
                </SpinnerButton>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
